import type { AxiosError } from "axios";

/**
 * The backend answers errors in more than one shape:
 *   { error: { message } }                              services (wrong password, user exists ...)
 *   { error: { validationErrors: { body: { message }}}} request validation
 *   { message }                                         middleware (captcha, rate limits, lockouts)
 * The screens read `data.error.message`, so every other shape fell through to a generic "failed" text, and a failed
 * request with no response at all (offline, blocked, timeout) crashed the code that destructures `response`.
 * This interceptor makes every failure carry its best message at `data.error.message`.
 */

export const NETWORK_MESSAGE = "We could not reach the server. Check your internet connection and try again.";
export const TIMEOUT_MESSAGE = "The server took too long to respond. Please try again.";
export const CAPTCHA_MESSAGE = "We could not complete the security check. Please refresh the page and try again. If it keeps happening, turn off any ad blocker for this site.";

const STATUS_MESSAGES: Record<number, string> = {
  400: "That request could not be processed. Please check what you entered and try again.",
  401: "Your details were not accepted or your session has expired. Please sign in again.",
  403: "You do not have permission to do that.",
  404: "We could not find what you asked for.",
  408: TIMEOUT_MESSAGE,
  409: "That already exists.",
  413: "That upload is too large.",
  429: "Too many attempts. Please wait a minute and try again.",
  502: "The server is temporarily unavailable. Please try again in a moment.",
  503: "The server is temporarily unavailable. Please try again in a moment.",
  504: TIMEOUT_MESSAGE,
};

function clean(value: unknown): string {
  if (typeof value !== "string") return "";
  const text = value.trim();
  // An HTML error page from a proxy is not a message for a shopper.
  if (!text || text.startsWith("<")) return "";
  // Joi quotes field names: "email" must be a valid email -> email must be a valid email
  return text.replace(/"([A-Za-z0-9_.[\]]+)"/g, "$1").slice(0, 300);
}

/** The best human-readable message in a backend error body, whatever its shape; "" when there is none. */
export function messageFromBody(body: unknown): string {
  if (typeof body === "string") return clean(body);
  if (!body || typeof body !== "object") return "";
  const b = body as Record<string, any>;
  const e = b.error;
  const candidates = [
    e?.validationErrors?.body?.message,
    e?.validationErrors?.query?.message,
    e?.validationErrors?.params?.message,
    e?.message,
    typeof e === "string" ? e : undefined,
    b.message,
    b.msg,
    Array.isArray(b.errors) ? b.errors[0]?.message ?? b.errors[0] : undefined,
    b.data?.message,
  ];
  for (const c of candidates) {
    const text = clean(c);
    if (text) return text;
  }
  return "";
}

export function statusMessage(status: number | undefined): string {
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (status && status >= 500) return "Something went wrong on our side. Please try again in a moment.";
  return "Something went wrong. Please try again.";
}

/** The body with its best message guaranteed at `error.message`; everything else in it is kept. */
export function normalizeErrorBody(body: unknown, status?: number): Record<string, any> {
  let message = messageFromBody(body) || statusMessage(status);
  // The bot check failing (blocked script, expired token) is common and the raw text means nothing to a shopper.
  if (/captcha/i.test(message)) message = CAPTCHA_MESSAGE;
  const base = body && typeof body === "object" && !Array.isArray(body) ? (body as Record<string, any>) : {};
  const error = base.error && typeof base.error === "object" ? base.error : {};
  if (typeof error.message === "string" && error.message.trim() === message) return base;
  return { ...base, error: { ...error, message } };
}

const BINARY = new Set(["blob", "arraybuffer", "stream"]);

export const normalizeApiErrorInterceptor = {
  onFulfilled: <T,>(response: T): T => response,
  onRejected: (error: AxiosError): Promise<never> => {
    const responseType = (error.config as any)?.responseType;
    if (responseType && BINARY.has(responseType)) return Promise.reject(error);
    if (!error.response) {
      const timedOut = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" || /timeout/i.test(error.message || "");
      // A response-shaped object, so the services that destructure `response` get a message instead of a TypeError.
      (error as any).response = {
        status: 0,
        statusText: "",
        headers: {},
        config: error.config,
        data: { error: { error: timedOut ? "Timeout" : "NetworkError", message: timedOut ? TIMEOUT_MESSAGE : NETWORK_MESSAGE } },
      };
    } else {
      error.response.data = normalizeErrorBody(error.response.data, error.response.status);
    }
    return Promise.reject(error);
  },
};
